import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icon/index.js';
import { apiGet, getApiOrigin } from '../../lib/api.js';
import { GatorMarkCircle } from '../shared/GatorMarkCircle.js';

type CampaignPlacement = 'DIALOG' | 'BANNER';

type PublicCampaign = {
  id: string;
  placement: CampaignPlacement;
  titleEn: string;
  titlePl: string;
  bodyEn: string | null;
  bodyPl: string | null;
  badgeEn: string | null;
  badgePl: string | null;
  ctaLabelEn: string | null;
  ctaLabelPl: string | null;
  ctaUrl: string | null;
  imageUrl: string | null;
};

const DISMISS_PREFIX = 'ig-campaign-dismissed-';
const DIALOG_DELAY_MS = 1600;

function dismissed(id: string): boolean {
  try {
    return localStorage.getItem(`${DISMISS_PREFIX}${id}`) === '1';
  } catch {
    return false;
  }
}

function dismiss(id: string) {
  try {
    localStorage.setItem(`${DISMISS_PREFIX}${id}`, '1');
  } catch {
    /* storage unavailable — the campaign simply reappears next visit */
  }
}

function resolveImage(url: string | null): string | null {
  if (!url) return null;
  const origin = getApiOrigin() ?? '';
  return url.startsWith('/') ? `${origin}${url}` : url;
}

function useLocalized(campaign: PublicCampaign | null) {
  const { i18n } = useTranslation();
  const pl = i18n.language?.startsWith('pl') ?? false;
  return useMemo(() => {
    if (!campaign) return null;
    return {
      title: pl ? campaign.titlePl : campaign.titleEn,
      body: pl ? campaign.bodyPl : campaign.bodyEn,
      badge: pl ? campaign.badgePl : campaign.badgeEn,
      ctaLabel: pl ? campaign.ctaLabelPl : campaign.ctaLabelEn,
    };
  }, [campaign, pl]);
}

function followCta(campaign: PublicCampaign) {
  dismiss(campaign.id);
  if (!campaign.ctaUrl) return;
  if (/^https?:\/\//i.test(campaign.ctaUrl)) {
    window.open(campaign.ctaUrl, '_blank', 'noopener');
  } else {
    // Full navigation keeps this component router-independent.
    window.location.href = campaign.ctaUrl;
  }
}

function CampaignBanner({ campaign, onDismiss }: { campaign: PublicCampaign; onDismiss: () => void }) {
  const copy = useLocalized(campaign);
  if (!copy) return null;

  return (
    <div
      className="relative z-[60] flex items-center justify-center gap-3 px-4 py-2.5 text-center"
      style={{ background: 'linear-gradient(90deg, #128A3E, #1DB954 55%, #128A3E)' }}
      role="region"
      aria-label={copy.title}
    >
      {copy.badge ? (
        <span className="hidden rounded-full bg-black/25 px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-white sm:inline-flex">
          {copy.badge}
        </span>
      ) : null}
      <p className="font-sans text-[13px] font-medium text-white">
        {copy.title}
      </p>
      {copy.ctaLabel ? (
        <button
          type="button"
          onClick={() => followCta(campaign)}
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-black/25 px-3 py-1 font-sans text-[12px] font-semibold text-white transition hover:bg-black/40"
        >
          {copy.ctaLabel}
          <Icon name="arrow-right" className="h-3 w-3" />
        </button>
      ) : null}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-white/80 transition hover:bg-black/25 hover:text-white"
      >
        <Icon name="x" className="h-4 w-4" />
      </button>
    </div>
  );
}

function CampaignDialog({ campaign, onDismiss }: { campaign: PublicCampaign; onDismiss: () => void }) {
  const copy = useLocalized(campaign);
  const image = resolveImage(campaign.imageUrl);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onDismiss]);

  if (!copy) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
      role="dialog"
      aria-modal="true"
      aria-label={copy.title}
      onClick={onDismiss}
    >
      <div
        className="relative w-full max-w-[460px] overflow-hidden rounded-lg border border-ig-border bg-ig-bg-secondary shadow-2xl motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-4 motion-safe:duration-400"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute right-3 top-3 z-10 rounded-full bg-black/40 p-1.5 text-white/90 backdrop-blur transition hover:bg-black/60"
        >
          <Icon name="x" className="h-4 w-4" />
        </button>

        {image ? (
          <img src={image} alt="" className="max-h-[240px] w-full object-cover" />
        ) : (
          <div className="flex h-28 items-center justify-center bg-gradient-to-br from-ig-bg-tertiary to-ig-accent-subtle">
            <GatorMarkCircle className="h-14 w-14" alt="" />
          </div>
        )}
        {/* Accent signature bar */}
        <div className="h-[3px] w-full" style={{ background: 'linear-gradient(90deg, #128A3E, #1DB954 45%, #6EE7A0)' }} />

        <div className="flex flex-col gap-3 p-6">
          {copy.badge ? (
            <span className="w-fit rounded-full bg-ig-accent-subtle px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-ig-accent">
              {copy.badge}
            </span>
          ) : null}
          <h2 className="font-display text-[26px] font-light leading-[1.15] text-ig-text-primary">{copy.title}</h2>
          {copy.body ? (
            <p className="font-sans text-[14px] leading-[1.65] text-ig-text-secondary">{copy.body}</p>
          ) : null}
          {copy.ctaLabel && campaign.ctaUrl ? (
            <button
              type="button"
              onClick={() => followCta(campaign)}
              className="mt-2 inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-ig-accent px-6 font-sans text-[14px] font-semibold text-[var(--ig-accent-text)] transition hover:brightness-110"
            >
              {copy.ctaLabel}
              <Icon name="arrow-right" className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Mounted once in the landing layout: fetches active admin-managed campaigns
 * and shows at most one banner (top strip) and one dialog (popup, after a
 * short delay). Dismissals persist per-campaign in localStorage, so users see
 * each campaign once until the admin publishes a new one.
 */
export function PromoCampaignHost() {
  const [banner, setBanner] = useState<PublicCampaign | null>(null);
  const [dialog, setDialog] = useState<PublicCampaign | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void apiGet<{ campaigns: PublicCampaign[] }>('/campaigns/active', { suppressErrorToast: true })
      .then((res) => {
        if (cancelled) return;
        const list = res.campaigns ?? [];
        setBanner(list.find((c) => c.placement === 'BANNER' && !dismissed(c.id)) ?? null);
        const dialogCampaign = list.find((c) => c.placement === 'DIALOG' && !dismissed(c.id)) ?? null;
        setDialog(dialogCampaign);
        if (dialogCampaign) {
          window.setTimeout(() => {
            if (!cancelled) setDialogVisible(true);
          }, DIALOG_DELAY_MS);
        }
      })
      .catch(() => {
        /* campaigns are cosmetic — never break the landing page */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      {banner ? (
        <CampaignBanner
          campaign={banner}
          onDismiss={() => {
            dismiss(banner.id);
            setBanner(null);
          }}
        />
      ) : null}
      {dialog && dialogVisible ? (
        <CampaignDialog
          campaign={dialog}
          onDismiss={() => {
            dismiss(dialog.id);
            setDialogVisible(false);
            setDialog(null);
          }}
        />
      ) : null}
    </>
  );
}
